(() => {
  if (customElements.get('edufun-product-3d')) return;
  const requests = new Map();
  const svgNS = 'http://www.w3.org/2000/svg';
  const runtimeRequests = new Map();
  const modelTag = 'edufun-model-viewer';
  const defaultOrbit = '-30deg 68deg 120%';
  const ensureRuntime = (assetURL, tailURL) => {
    if (customElements.get(modelTag)) return Promise.resolve();
    let urls;
    try {
      const page = new URL(document.baseURI);
      urls = [assetURL, tailURL].map(value => {
        if (!value) throw new Error('Missing runtime URL');
        const url = new URL(value, document.baseURI);
        const localFixture = url.origin === page.origin && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
        if (url.username || url.password || url.protocol !== 'https:' && !localFixture) throw new Error('Invalid runtime URL');
        return url.href;
      });
    } catch { return Promise.reject(new Error('Invalid runtime URL')); }
    const key = urls.join('|');
    if (runtimeRequests.has(key)) return runtimeRequests.get(key);
    const request = Promise.all(urls.map(url => import(url))).then(async parts => {
      if (!parts.every(part => typeof part.default === 'string' && part.default.length > 1000)) throw new Error('Invalid runtime parts');
      const blobURL = URL.createObjectURL(new Blob(parts.map(part => part.default), { type: 'text/javascript' }));
      try { await import(blobURL); } finally { URL.revokeObjectURL(blobURL); }
      if (!customElements.get(modelTag)) throw new Error('3D runtime unavailable');
    }).catch(error => { runtimeRequests.delete(key); throw error; });
    runtimeRequests.set(key, request);
    return request;
  };
  const getMeasurements = assetURL => {
    let url;
    try {
      url = new URL(assetURL, document.baseURI);
      if (url.protocol !== 'https:' || url.username || url.password) return Promise.resolve(null);
    } catch { return Promise.resolve(null); }
    if (requests.has(url.href)) return requests.get(url.href);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    const request = fetch(url.href, { credentials: 'omit', signal: controller.signal })
      .then(response => { if (!response.ok) throw new Error('Measurements unavailable'); return response.json(); })
      .then(data => data?.version === 1 && data.models && typeof data.models === 'object' ? data.models : null)
      .catch(() => { requests.delete(url.href); return null; })
      .finally(() => clearTimeout(timeout));
    requests.set(url.href, request);
    return request;
  };
  class EduFunProduct3D extends HTMLElement {
    static validPair(pair) {
      return Array.isArray(pair) && pair.length === 2 && pair.every(point => Array.isArray(point) && point.length === 3 && point.every(value => typeof value === 'number' && Number.isFinite(value))) && pair[0].some((value, index) => value !== pair[1][index]);
    }
    static measurementFor(models, mediaId, productId) {
      if (!/^\d+$/.test(String(mediaId)) || !models || !Object.hasOwn(models, mediaId)) return null;
      const entry = models[mediaId];
      if (!entry || String(entry.productId) !== String(productId) || !Array.isArray(entry.dimensions) || !entry.dimensions.length) return null;
      if (typeof entry.modelLabel !== 'string' || !entry.modelLabel || typeof entry.sku !== 'string' || !entry.sku) return null;
      if (!entry.dimensions.every(d => d && typeof d.label === 'string' && d.label.trim().length > 0 && d.label.length <= 100 && typeof d.value === 'number' && Number.isFinite(d.value) && d.value > 0 && d.unit === 'cm')) return null;
      return {
        modelLabel: entry.modelLabel,
        sku: entry.sku,
        note: typeof entry.note === 'string' ? entry.note.trim() : '',
        dimensions: entry.dimensions.map(d => ({ key: d.key, label: d.label, value: d.value, unit: d.unit, anchors: this.validPair(d.anchors) ? d.anchors : null, extensionPoints: this.validPair(d.extensionPoints) ? d.extensionPoints : null }))
      };
    }
    static projectionFor(a, b, width, height, labelWidth, occupied = []) {
      const visible = p => p && Number.isFinite(p.x) && Number.isFinite(p.y) && (p.z === undefined || Number.isFinite(p.z) && p.z >= -1 && p.z <= 1) && p.x >= 4 && p.y >= 4 && p.x <= width - 4 && p.y <= height - 4;
      if (!visible(a) || !visible(b) || width < 100 || height < 100) return null;
      const dx = b.x - a.x, dy = b.y - a.y, distance = Math.hypot(dx, dy);
      if (distance < 18) return null;
      const nx = -dy / distance, ny = dx / distance, boxWidth = Math.min(labelWidth, width - 24), boxHeight = 27;
      const middle = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      let box;
      for (const offset of [20, -20, 48, -48, 76, -76]) {
        const x = Math.max(12, Math.min(width - boxWidth - 12, middle.x + nx * offset - boxWidth / 2));
        const y = Math.max(12, Math.min(height - boxHeight - 12, middle.y + ny * offset - boxHeight / 2));
        box = { x, y, width: boxWidth, height: boxHeight };
        if (!occupied.some(other => x < other.x + other.width + 5 && x + boxWidth + 5 > other.x && y < other.y + other.height + 5 && y + boxHeight + 5 > other.y)) break;
      }
      return { a, b, nx, ny, box };
    }
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      if (!this.dialog?.showModal) return;
      this.stage = this.querySelector('[data-edufun-stage]');
      this.state = this.querySelector('[data-edufun-state]');
      this.poster = this.querySelector('[data-edufun-poster]');
      this.spinner = this.querySelector('[data-edufun-spinner]');
      this.status = this.querySelector('[data-edufun-status]');
      this.retry = this.querySelector('[data-edufun-retry]');
      this.controls = this.querySelector('[data-edufun-controls]');
      this.hint = this.querySelector('[data-edufun-hint]');
      this.dimensionToggle = this.querySelector('[data-edufun-dimensions-toggle]');
      this.toolbarSeparator = this.querySelector('[data-edufun-toolbar-sep]');
      this.arButton = this.querySelector('[data-edufun-ar]');
      this.fallback = this.querySelector('[data-edufun-fallback]');
      this.chips = [...this.querySelectorAll('[data-edufun-chip]')];
      this.templates = [...this.querySelectorAll('template[data-edufun-model]')];
      this.variants = this.readVariants();
      this.dimensionsVisible = true;
      this.triggerController = new AbortController();
      const signal = this.triggerController.signal;
      document.addEventListener('click', event => {
        const trigger = event.target.closest?.('[data-edufun-3d-open]');
        if (trigger?.getAttribute('data-edufun-3d-open') !== this.dataset.productId) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(trigger);
      }, { signal, capture: true });
      this.querySelector('[data-edufun-close]').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, { signal });
      this.dialog.addEventListener('close', () => {
        cancelAnimationFrame(this.overlayFrame);
        this.overlayFrame = null;
        this.hideHint();
        document.body.style.overflow = this.previousOverflow || '';
        this.opener?.focus();
      }, { signal });
      this.querySelector('[data-edufun-reset]').addEventListener('click', () => this.resetCamera(), { signal });
      this.querySelectorAll('[data-edufun-zoom]').forEach(button => button.addEventListener('click', () => {
        const orbit = this.viewer?.getCameraOrbit?.();
        if (!orbit) return;
        const factor = button.dataset.edufunZoom === 'in' ? 0.8 : 1.25;
        this.viewer.cameraOrbit = `${orbit.theta}rad ${orbit.phi}rad ${orbit.radius * factor}m`;
        this.scheduleOverlay();
      }, { signal }));
      this.retry.addEventListener('click', () => this.loadModel(this.currentIndex || 0), { signal });
      this.chips.forEach(chip => chip.addEventListener('click', () => {
        this.fallback.hidden = true;
        const index = Number(chip.dataset.edufunChip);
        if (index !== this.currentIndex) this.loadModel(index);
      }, { signal }));
      this.dimensionToggle.addEventListener('click', () => {
        this.dimensionsVisible = !this.dimensionsVisible;
        this.dimensionToggle.setAttribute('aria-pressed', String(this.dimensionsVisible));
        this.updateOverlayVisibility();
      }, { signal });
      this.arButton.addEventListener('click', () => this.viewer?.activateAR?.(), { signal });
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleOverlay());
        this.resizeObserver.observe(this.stage);
      }
      window.addEventListener('resize', () => this.scheduleOverlay(), { signal });
      this.activateTriggers();
      document.addEventListener('shopify:section:load', () => this.activateTriggers(), { signal });
    }
    activateTriggers() {
      document.querySelectorAll(`[data-edufun-3d-open="${this.dataset.productId}"]`).forEach(button => {
        button.hidden = false;
        button.parentElement.classList.add('edufun-3d-gallery');
      });
    }
    readVariants() {
      try {
        const variants = JSON.parse(this.querySelector('[data-edufun-variants]')?.textContent || '[]');
        return Array.isArray(variants) ? variants : [];
      } catch { return []; }
    }
    selectedVariant() {
      if (!this.variants.length) return null;
      const byId = new Map(this.variants.map(variant => [String(variant.id), variant]));
      for (const input of document.querySelectorAll('form[action*="/cart/add"] [name="id"]')) {
        if (byId.has(input.value)) return byId.get(input.value);
      }
      return byId.get(new URLSearchParams(location.search).get('variant')) || null;
    }
    open(trigger) {
      this.opener = trigger;
      const variant = this.selectedVariant();
      let index = variant?.sku ? this.templates.findIndex(template => template.dataset.sku === variant.sku) : -1;
      const matched = index >= 0;
      if (!matched) index = Number.isInteger(this.currentIndex) ? this.currentIndex : 0;
      this.showFallback(!matched && this.variants.length > 1 ? variant : null, index);
      if (!this.dialog.open) {
        this.previousOverflow = document.body.style.overflow;
        this.dialog.showModal();
        document.body.style.overflow = 'hidden';
      }
      if (!this.viewer || index !== this.currentIndex) this.loadModel(index);
      else this.scheduleOverlay();
    }
    showFallback(variant, index) {
      this.fallback.hidden = !variant;
      if (!variant) return;
      this.fallback.textContent = this.fallback.dataset.template
        .replace('__SELECTED__', variant.label)
        .replace('__SHOWN__', this.templates[index].dataset.label);
    }
    resetCamera() {
      if (!this.viewer) return;
      this.viewer.cameraOrbit = defaultOrbit;
      this.viewer.cameraTarget = 'auto auto auto';
      this.viewer.fieldOfView = 'auto';
      this.scheduleOverlay();
    }
    showHint() {
      if (this.hintShown) return;
      this.hintShown = true;
      this.hint.hidden = false;
      requestAnimationFrame(() => this.hint.classList.add('is-visible'));
      this.hintTimer = setTimeout(() => this.hideHint(), 5000);
    }
    hideHint() {
      clearTimeout(this.hintTimer);
      this.hint.classList.remove('is-visible');
    }
    setState(mode, poster) {
      this.state.hidden = mode === 'ready';
      this.state.dataset.mode = mode;
      this.spinner.hidden = mode !== 'loading';
      this.retry.hidden = mode !== 'error';
      this.status.textContent = mode === 'ready' ? '' : this.status.dataset[mode];
      if (poster !== undefined) {
        this.poster.hidden = !poster;
        if (poster) this.poster.src = poster;
        else this.poster.removeAttribute('src');
      }
    }
    async updateMeasurements() {
      const template = this.templates[this.currentIndex || 0];
      const requestId = this.measurementRequest = (this.measurementRequest || 0) + 1;
      const models = await getMeasurements(this.dataset.measurementsUrl);
      if (requestId !== this.measurementRequest || !this.isConnected) return;
      this.currentEntry = EduFunProduct3D.measurementFor(models, template.dataset.mediaId, this.dataset.productId);
      const panel = this.querySelector('[data-edufun-measurements]');
      panel.hidden = !this.currentEntry;
      if (this.currentEntry) {
        const entry = this.currentEntry;
        this.querySelector('[data-edufun-measurement-sku]').textContent = entry.sku;
        const list = this.querySelector('[data-edufun-measurement-list]');
        list.replaceChildren();
        entry.dimensions.forEach(dimension => {
          const row = document.createElement('div');
          const label = document.createElement('dt');
          const value = document.createElement('dd');
          label.textContent = dimension.label;
          value.textContent = this.formatDimension(dimension);
          row.append(label, value);
          list.append(row);
        });
        const note = this.querySelector('[data-edufun-measurement-note]');
        note.textContent = entry.note;
        note.hidden = !entry.note;
      }
      this.buildOverlay();
    }
    formatDimension(dimension) {
      return `${new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 3 }).format(dimension.value)} ${dimension.unit}`;
    }
    clearOverlay() {
      cancelAnimationFrame(this.overlayFrame);
      this.overlayFrame = null;
      this.overlay?.remove();
      this.overlay = null;
      this.hotspots?.forEach(hotspot => hotspot.remove());
      this.hotspots = [];
      this.overlayItems = [];
    }
    buildOverlay() {
      this.clearOverlay();
      const dimensions = this.currentEntry?.dimensions.filter(dimension => dimension.anchors) || [];
      const supported = typeof this.viewer?.queryHotspot === 'function';
      this.dimensionToggle.hidden = !dimensions.length || this.viewerLoaded && !supported;
      this.toolbarSeparator.hidden = this.dimensionToggle.hidden;
      this.dimensionToggle.disabled = !this.viewerLoaded || !supported;
      this.dimensionToggle.setAttribute('aria-pressed', String(!!this.dimensionsVisible));
      if (!dimensions.length || !this.viewerLoaded || !supported) return;
      const svg = document.createElementNS(svgNS, 'svg');
      svg.classList.add('edufun-3d-dimension-overlay');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');
      svg.setAttribute('aria-hidden', 'true');
      const createSVG = (tag, className) => {
        const element = document.createElementNS(svgNS, tag);
        if (className) element.setAttribute('class', className);
        return element;
      };
      const hotspot = (point, name) => {
        const element = document.createElement('span');
        element.slot = name;
        element.className = 'edufun-3d-anchor';
        element.setAttribute('data-position', point.map(value => `${value}m`).join(' '));
        element.setAttribute('data-normal', '0 1 0');
        element.setAttribute('aria-hidden', 'true');
        this.viewer.append(element);
        this.hotspots.push(element);
        return name;
      };
      this.overlayItems = dimensions.map((dimension, index) => {
        const start = hotspot(dimension.anchors[0], `hotspot-edufun-${index}-start`);
        const end = hotspot(dimension.anchors[1], `hotspot-edufun-${index}-end`);
        const extension = dimension.extensionPoints?.map((point, endpoint) => hotspot(point, `hotspot-edufun-${index}-surface-${endpoint}`));
        const group = createSVG('g');
        group.style.display = 'none';
        const extensionLines = extension?.map(() => createSVG('line', 'edufun-3d-extension-line')) || [];
        const line = createSVG('line', 'edufun-3d-dimension-line');
        const ticks = [createSVG('line', 'edufun-3d-dimension-tick'), createSVG('line', 'edufun-3d-dimension-tick')];
        const box = createSVG('rect', 'edufun-3d-dimension-box');
        box.setAttribute('rx', '6');
        const text = createSVG('text', 'edufun-3d-dimension-text');
        text.textContent = `${dimension.label} ${this.formatDimension(dimension)}`;
        text.setAttribute('text-anchor', 'middle');
        text.setAttribute('dominant-baseline', 'central');
        group.append(...extensionLines, line, ...ticks, box, text);
        svg.append(group);
        return { start, end, extension, extensionLines, group, line, ticks, box, text, labelWidth: Math.max(72, text.textContent.length * 6.6 + 22) };
      });
      this.viewer.append(svg);
      this.overlay = svg;
      this.projectionRetries = 3;
      this.updateOverlayVisibility();
    }
    updateOverlayVisibility() {
      if (!this.overlay) return;
      this.overlay.toggleAttribute('hidden', !this.dimensionsVisible);
      if (this.dimensionsVisible) { this.projectionRetries = 3; this.scheduleOverlay(); }
    }
    scheduleOverlay() {
      if (this.overlayFrame != null || !this.dialog?.open || !this.dimensionsVisible || !this.overlay) return;
      this.overlayFrame = requestAnimationFrame(() => {
        this.overlayFrame = null;
        this.renderOverlay();
      });
    }
    renderOverlay() {
      if (!this.viewerLoaded || !this.dialog?.open || !this.dimensionsVisible || !this.overlay) return;
      const width = this.viewer.clientWidth, height = this.viewer.clientHeight;
      this.overlay.setAttribute('viewBox', `0 0 ${width} ${height}`);
      const occupied = [];
      let notRegistered = false;
      const pointFor = name => {
        try { return this.viewer.queryHotspot(name)?.canvasPosition; } catch { return null; }
      };
      const setLine = (line, a, b) => {
        line.setAttribute('x1', a.x); line.setAttribute('y1', a.y);
        line.setAttribute('x2', b.x); line.setAttribute('y2', b.y);
      };
      this.overlayItems.forEach(item => {
        const a = pointFor(item.start), b = pointFor(item.end);
        if (!a || !b) notRegistered = true;
        const projection = EduFunProduct3D.projectionFor(a, b, width, height, item.labelWidth, occupied);
        item.group.style.display = projection ? '' : 'none';
        if (!projection) return;
        occupied.push(projection.box);
        setLine(item.line, a, b);
        [a, b].forEach((point, index) => setLine(item.ticks[index], { x: point.x - projection.nx * 5, y: point.y - projection.ny * 5 }, { x: point.x + projection.nx * 5, y: point.y + projection.ny * 5 }));
        item.extensionLines.forEach((line, index) => {
          const point = pointFor(item.extension[index]);
          const target = index ? b : a;
          const valid = point && Number.isFinite(point.x) && Number.isFinite(point.y) && (point.z === undefined || point.z >= -1 && point.z <= 1);
          line.style.display = valid ? '' : 'none';
          if (valid) setLine(line, point, target);
        });
        for (const [key, value] of Object.entries(projection.box)) item.box.setAttribute(key, value);
        item.text.setAttribute('x', projection.box.x + projection.box.width / 2);
        item.text.setAttribute('y', projection.box.y + projection.box.height / 2);
      });
      if (notRegistered && this.projectionRetries-- > 0) this.scheduleOverlay();
    }
    async loadModel(index) {
      if (!Number.isInteger(index) || !this.templates[index]) return;
      clearTimeout(this.timer);
      this.modelController?.abort();
      this.clearOverlay();
      const loadId = this.modelRequest = (this.modelRequest || 0) + 1;
      this.modelController = new AbortController();
      const signal = this.modelController.signal;
      const template = this.templates[index];
      this.currentIndex = index;
      this.currentEntry = null;
      this.viewerLoaded = false;
      this.viewer = null;
      this.stage.replaceChildren();
      this.chips.forEach(chip => chip.setAttribute('aria-pressed', String(Number(chip.dataset.edufunChip) === index)));
      this.dimensionToggle.hidden = true;
      this.toolbarSeparator.hidden = true;
      this.dimensionToggle.disabled = true;
      this.arButton.hidden = true;
      this.querySelector('[data-edufun-measurements]').hidden = true;
      this.controls.hidden = true;
      this.setState('loading', template.dataset.poster || '');
      const failure = () => {
        if (loadId !== this.modelRequest) return;
        this.viewerLoaded = false;
        this.clearOverlay();
        this.dimensionToggle.disabled = true;
        this.controls.hidden = true;
        this.setState('error');
        clearTimeout(this.timer);
      };
      this.timer = setTimeout(failure, 45000);
      this.updateMeasurements();
      try { await ensureRuntime(this.dataset.runtimeUrl, this.dataset.runtimeTailUrl); } catch { failure(); return; }
      if (loadId !== this.modelRequest || !this.isConnected) return;
      const source = template.content.querySelector('model-viewer');
      if (!source) { failure(); return; }
      const viewer = document.createElement(modelTag);
      for (const attribute of [...source.attributes]) {
        if (!['data-shopify-feature', 'id', 'style'].includes(attribute.name)) viewer.setAttribute(attribute.name, attribute.value);
      }
      viewer.setAttribute('camera-orbit', defaultOrbit);
      viewer.setAttribute('ar', '');
      viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
      viewer.setAttribute('ar-scale', 'fixed');
      viewer.setAttribute('ar-placement', 'floor');
      if (template.dataset.iosSrc) viewer.setAttribute('ios-src', template.dataset.iosSrc);
      this.viewer = viewer;
      const arSlot = document.createElement('span');
      arSlot.slot = 'ar-button';
      viewer.append(arSlot);
      viewer.addEventListener('load', () => {
        if (this.viewer !== viewer || loadId !== this.modelRequest) return;
        clearTimeout(this.timer);
        this.viewerLoaded = true;
        this.setState('ready');
        this.controls.hidden = false;
        this.arButton.hidden = !viewer.canActivateAR;
        this.buildOverlay();
        this.showHint();
      }, { signal, once: true });
      viewer.addEventListener('error', failure, { signal, once: true });
      viewer.addEventListener('camera-change', event => {
        this.scheduleOverlay();
        if (event.detail?.source === 'user-interaction') this.hideHint();
      }, { signal });
      this.stage.replaceChildren(viewer);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.hintTimer);
      this.clearOverlay();
      this.measurementRequest = (this.measurementRequest || 0) + 1;
      this.modelRequest = (this.modelRequest || 0) + 1;
      this.triggerController?.abort();
      this.modelController?.abort();
      this.resizeObserver?.disconnect();
      if (this.dialog?.open) { this.dialog.close(); document.body.style.overflow = this.previousOverflow || ''; }
      this.ready = false;
    }
  }
  customElements.define('edufun-product-3d', EduFunProduct3D);
})();
